/**
 * Iter 97.19 — Builder Template normalizer.
 *
 * The Commercials Builder keeps `commercials.itemPrices` + `amc.items`
 * keyed by per-prototype `scopeRowId` UUIDs. Those IDs are meaningless
 * in any other proposal — re-applying a template into a fresh proposal
 * requires the keys to be STABLE across proposals.
 *
 * Stable key = `${productCode}|${moduleCode || "*"}` (atomic products
 * use `*` as the module slot so the encoder is total).
 *
 * On SAVE  → flatten scope into rows carrying (productCode, moduleCode,
 *            productLabel, moduleLabel) so the apply path doesn't need
 *            to look anything up in the live Product Master; convert
 *            itemPrices / amc.items into the stable-key shape.
 *
 * On APPLY → rebuild a new scope list with freshly-minted `scopeRowId`s
 *            against the live Product Master, re-link the prices/amc
 *            keyed by stable-key to the new row IDs.
 *
 * Placeholder substitution happens at apply time inside the T&C / header
 * text fields so the resulting Builder state already contains the
 * client-specific text (the operator sees the resolved copy).
 */

import { newScopeId, DELIVERY_MODES } from "./builderStorage";

const VALID_DM = new Set(DELIVERY_MODES.map((m) => m.code));

const stableKey = (productCode, moduleCode) =>
    `${productCode || ""}|${moduleCode || "*"}`;

/* ─────────────────────────────────────────────────────────────────────
   SAVE — flatten the live Builder state into a portable template body.
   ───────────────────────────────────────────────────────────────────── */

export function buildTemplateSectionsFromPrototype(prototype, includes) {
    if (!prototype) return {};
    const out = {};

    if (includes.has("scope")) {
        out.scope = (prototype.scope || []).map((r) => ({
            productCode: r.productCode || r.code || "",
            moduleCode: r.moduleCode || null,
            // For atomic rows productName == product label; for module rows
            // productName == module label and groupLabel carries the parent
            // product label. Persist both so apply can rebuild labels.
            productLabel: r.groupLabel || r.productName || "",
            moduleLabel: r.moduleCode ? (r.productName || "") : null,
            categoryCode: r.categoryCode || null,
            categoryLabel: r.categoryLabel || null,
        })).filter((r) => r.productCode);
    }

    // commercials + amc are bundled — saving commercials without AMC would
    // strand recurring fees, so the UI presents them together. Operator can
    // still uncheck AMC explicitly to save only setup pricing.
    if (includes.has("commercials")) {
        const m = prototype.commercials || {};
        const scopeById = new Map((prototype.scope || []).map((r) => [r.id, r]));

        const itemPricesByCode = {};
        Object.entries(m.itemPrices || {}).forEach(([rowId, price]) => {
            const row = scopeById.get(rowId);
            if (!row) return;
            const k = stableKey(row.productCode || row.code, row.moduleCode);
            if (Number(price) > 0) itemPricesByCode[k] = Number(price);
        });

        // Groups carry productKeys (== productCode in the live builder).
        const groupsByCode = (m.groups || []).map((g) => ({
            id: g.id,
            label: g.label || "",
            productCodes: Array.isArray(g.productKeys) ? [...g.productKeys] : [],
        }));

        // Delivery modes per product → keyed by productCode, filtered to
        // the 5-code vocabulary (rejects anything stale).
        const deliveryModesByCode = {};
        Object.entries(m.deliveryModes || {}).forEach(([productKey, codes]) => {
            const filtered = (codes || []).filter((c) => VALID_DM.has(c));
            if (filtered.length > 0) deliveryModesByCode[productKey] = filtered;
        });

        out.commercials = {
            itemPricesByCode,
            presentationMode: m.presentationMode || "itemized",
            deliveryModes: deliveryModesByCode,
            // Iter 97.20 — discount carries the full Special Offer metadata
            // (label / reasonCode / notes). Placeholders in `label` are
            // resolved at apply time via _walkResolve below.
            discount: {
                type: m.discount?.type || "percent",
                value: Number(m.discount?.value) || 0,
                label: (m.discount?.label || m.discount?.reason || "").trim() || "",
                reasonCode: m.discount?.reasonCode || "",
                notes: m.discount?.notes || "",
            },
            groups: groupsByCode,
        };
    }

    if (includes.has("amc")) {
        const scopeById = new Map((prototype.scope || []).map((r) => [r.id, r]));
        const itemAmcByCode = {};
        ((prototype.amc || {}).items || []).forEach((item) => {
            const row = scopeById.get(item.scopeRowId);
            if (!row) return;
            const k = stableKey(row.productCode || row.code, row.moduleCode);
            if (Number(item.amount) > 0) itemAmcByCode[k] = Number(item.amount);
        });
        out.amc = { itemAmcByCode };
    }

    if (includes.has("tax_display")) {
        out.tax_display = { ...(prototype.taxDisplay || {}) };
    }

    if (includes.has("tnc")) {
        out.tnc = { ...(prototype.tnc || {}) };
    }

    return out;
}


/* ─────────────────────────────────────────────────────────────────────
   PLACEHOLDER RESOLVER — runs at apply time against the proposal context.
   Unknown tokens pass through verbatim so operator can use the literal
   text (e.g., {{LEGACY_TOKEN}}) for documentation.
   ───────────────────────────────────────────────────────────────────── */

const ALLOWED_TOKENS = new Set([
    "{{CLIENT_NAME}}",
    "{{CLIENT_ADDRESS}}",
    "{{CLIENT_GSTIN}}",
    "{{CONTACT_PERSON}}",
    "{{PROPOSAL_DATE}}",
    "{{VALIDITY_DATE}}",
]);

export function resolvePlaceholders(text, ctx) {
    if (typeof text !== "string" || !text) return text;
    const map = {
        "{{CLIENT_NAME}}":     ctx?.clientName     || "",
        "{{CLIENT_ADDRESS}}":  ctx?.clientAddress  || "",
        "{{CLIENT_GSTIN}}":    ctx?.clientGstin    || "",
        "{{CONTACT_PERSON}}":  ctx?.contactPerson  || "",
        "{{PROPOSAL_DATE}}":   ctx?.proposalDate   || "",
        "{{VALIDITY_DATE}}":   ctx?.validityDate   || "",
    };
    return text.replace(/\{\{[A-Z_]+\}\}/g, (m) => (ALLOWED_TOKENS.has(m) ? map[m] : m));
}

function _walkResolve(value, ctx) {
    if (typeof value === "string") return resolvePlaceholders(value, ctx);
    if (Array.isArray(value)) return value.map((v) => _walkResolve(v, ctx));
    if (value && typeof value === "object") {
        const o = {};
        Object.entries(value).forEach(([k, v]) => { o[k] = _walkResolve(v, ctx); });
        return o;
    }
    return value;
}


/* ─────────────────────────────────────────────────────────────────────
   APPLY DIFF — compute the patch that will be applied. Used by the
   confirm dialog so the operator can review BEFORE it lands.
   ───────────────────────────────────────────────────────────────────── */

export function diffTemplateAgainstPrototype(template, prototype) {
    const currentScope = prototype?.scope || [];
    const currentByCode = new Map();
    currentScope.forEach((r) => {
        currentByCode.set(stableKey(r.productCode || r.code, r.moduleCode), r);
    });

    const diff = {
        scope: { add: [], unchanged: [] },
        commercials: { update: [], add: [], removed: [] },
        amc: { update: [], add: [] },
        tax_display: null,
        tnc: null,
    };

    if (Array.isArray(template.scope)) {
        template.scope.forEach((row) => {
            const k = stableKey(row.productCode, row.moduleCode);
            if (currentByCode.has(k)) diff.scope.unchanged.push(row);
            else diff.scope.add.push(row);
        });
    }

    if (template.commercials) {
        const prices = template.commercials.itemPricesByCode || {};
        Object.entries(prices).forEach(([k, v]) => {
            const cur = currentByCode.get(k);
            if (cur) diff.commercials.update.push({ key: k, price: v, label: cur.moduleLabel || cur.productName });
            else diff.commercials.add.push({ key: k, price: v });
        });
    }

    if (template.amc?.itemAmcByCode) {
        Object.entries(template.amc.itemAmcByCode).forEach(([k, v]) => {
            const cur = currentByCode.get(k);
            if (cur) diff.amc.update.push({ key: k, amount: v, label: cur.moduleLabel || cur.productName });
            else diff.amc.add.push({ key: k, amount: v });
        });
    }

    if (template.tax_display) diff.tax_display = template.tax_display;
    if (template.tnc)         diff.tnc         = template.tnc;

    return diff;
}


/* ─────────────────────────────────────────────────────────────────────
   APPLY — re-link the template's stable-keyed payloads against the
   current scope (auto-adding any missing products). Returns the next
   prototype patch the Builder should write.
   ───────────────────────────────────────────────────────────────────── */

export function applyTemplateToPrototype(template, prototype, ctx) {
    if (!prototype) return prototype;
    const next = JSON.parse(JSON.stringify(prototype));
    next.scope = next.scope || [];

    /* Build a (productCode|moduleCode) → row index map for the current scope
       so we can look up existing rows when materialising prices. New rows
       added below extend this map in-place. */
    const indexByCode = new Map();
    next.scope.forEach((r) => {
        indexByCode.set(stableKey(r.productCode || r.code, r.moduleCode), r);
    });

    const ensureRow = (productCode, moduleCode, productLabel, moduleLabel) => {
        const k = stableKey(productCode, moduleCode);
        if (indexByCode.has(k)) return indexByCode.get(k);
        const isModule = !!moduleCode;
        const row = {
            id: newScopeId(),
            source: "product_master",
            kind: "service",
            capabilities: [],
            assetClass: "",
            productMasterCode: isModule ? moduleCode : productCode,
            productCode,
            moduleCode: moduleCode || null,
            groupCode: productCode,
            groupLabel: productLabel || productCode,
            categoryCode: null,
            categoryLabel: null,
            // Live builder convention: productName is the LEAF label
            // (module's own label for module rows; product's label for atomic).
            productName: isModule ? (moduleLabel || moduleCode) : (productLabel || productCode),
            subtitle: "",
        };
        next.scope.push(row);
        indexByCode.set(k, row);
        return row;
    };

    /* SCOPE — ADD missing template products. Never deletes operator's
       existing selections (apply-additive contract). */
    if (Array.isArray(template.scope)) {
        template.scope.forEach((row) => {
            ensureRow(row.productCode, row.moduleCode, row.productLabel, row.moduleLabel);
        });
    }

    /* COMMERCIALS — replace itemPrices for matching rows; auto-add any
       missing products so the template's prices land somewhere. Stable
       keys are resolved against the rebuilt index map. */
    if (template.commercials) {
        next.commercials = next.commercials || {};
        const tc = template.commercials;
        const itemPrices = { ...(next.commercials.itemPrices || {}) };
        Object.entries(tc.itemPricesByCode || {}).forEach(([k, v]) => {
            // k is "productCode|moduleCode" but we don't know labels for
            // auto-add — fall back to using the productCode as the label.
            const [pc, mc] = k.split("|");
            const row = ensureRow(pc, mc === "*" ? null : mc, pc, mc === "*" ? null : mc);
            itemPrices[row.id] = Number(v) || 0;
        });
        next.commercials.itemPrices = itemPrices;
        if (tc.presentationMode) next.commercials.presentationMode = tc.presentationMode;
        if (tc.discount) {
            // Iter 97.20 — resolve placeholders inside the discount label so
            // a template carrying "Onboarding offer for {{CLIENT_NAME}}"
            // resolves correctly when applied to a new proposal context.
            next.commercials.discount = _walkResolve({ ...tc.discount }, ctx);
        }
        if (tc.deliveryModes) {
            next.commercials.deliveryModes = { ...(next.commercials.deliveryModes || {}), ...tc.deliveryModes };
        }
        if (Array.isArray(tc.groups)) {
            next.commercials.groups = tc.groups.map((g) => ({
                id: g.id,
                label: g.label,
                productKeys: [...(g.productCodes || [])],
            }));
        }
    }

    /* AMC — same shape as commercials. */
    if (template.amc?.itemAmcByCode) {
        next.amc = next.amc || { items: [] };
        const items = [...(next.amc.items || [])];
        const byRowId = new Map(items.map((i) => [i.scopeRowId, i]));
        Object.entries(template.amc.itemAmcByCode).forEach(([k, v]) => {
            const [pc, mc] = k.split("|");
            const row = ensureRow(pc, mc === "*" ? null : mc, pc, mc === "*" ? null : mc);
            if (byRowId.has(row.id)) {
                byRowId.get(row.id).amount = Number(v) || 0;
            } else {
                const item = { scopeRowId: row.id, amount: Number(v) || 0 };
                items.push(item);
                byRowId.set(row.id, item);
            }
        });
        next.amc.items = items;
    }

    /* TAX DISPLAY — full replace. */
    if (template.tax_display) {
        next.taxDisplay = { ...template.tax_display };
    }

    /* T&C — placeholder-resolved full replace. */
    if (template.tnc) {
        next.tnc = _walkResolve(template.tnc, ctx);
    }

    return next;
}
